import React, { useState } from 'react';
import {
  AnalysisBlocks, Change, EvidenceList, SkeletonCards, formatKst, useAnalysisRequest,
} from './marketParts';

// 로컬 검증 때만 REACT_APP_COIN_API_URL 로 바꾼다. 비워 두면 운영 주소.
const COIN_API_URL = process.env.REACT_APP_COIN_API_URL
  || 'https://wbl8e2qc9a.execute-api.ap-northeast-2.amazonaws.com/coin/genai-coin-analisys';

const RANGES = [
  { id: 'first_5', label: '거래대금 1위 ~ 5위' },
  { id: 'last_5', label: '거래대금 6위 ~ 10위' },
];

const formatWon = (v) => {
  if (v == null) return '-';
  if (v >= 100) return `${Math.round(v).toLocaleString('ko-KR')}원`;
  return `${Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}원`;
};

const formatEok = (v) => (v == null ? '-' : `${Math.round(v / 1e8).toLocaleString('ko-KR')}억 원`);

const CoinCard = ({ coin }) => {
  const idPrefix = `coin-${coin.symbol}`;
  return (
    <article className="paper-card mkt-card">
      <div className="paper-rank" aria-hidden="true">{String(coin.rank).padStart(2, '0')}</div>
      <div className="paper-body">
        <h3 className="paper-title mkt-title">
          {coin.name_ko} <span className="mkt-symbol">{coin.symbol}</span>
        </h3>
        {coin.headline && <p className="paper-headline">{coin.headline}</p>}

        <div className="mkt-price-row">
          <span className="mkt-price">{formatWon(coin.price)}</span>
          <ul className="mkt-changes">
            <Change label="오늘(09시 기준)" value={coin.change_today} />
            <Change label="24시간" value={coin.change_24h} />
            <Change label="7일" value={coin.change_7d} />
            <Change label="30일" value={coin.change_30d} />
          </ul>
        </div>

        <ul className="paper-meta">
          <li>거래대금 {formatEok(coin.trade_value_24h)}</li>
          {coin.volume_ratio != null && <li>20일 평균의 {coin.volume_ratio.toFixed(1)}배</li>}
          {coin.warning && <li className="mkt-flag danger">업비트 유의 종목</li>}
          {(coin.cautions || []).map((c) => <li key={c} className="mkt-flag">{c} 주의</li>)}
          {coin.headline && !coin.news_explains && <li className="mkt-flag muted">뚜렷한 뉴스 근거 없음</li>}
        </ul>

        <AnalysisBlocks item={coin} idPrefix={idPrefix} />
        <EvidenceList articles={coin.articles} idPrefix={idPrefix} />

        <details className="paper-details">
          <summary>지표 더 보기</summary>
          <ul className="mkt-more">
            {coin.volatility_20d != null && <li>20일 일간 변동성 {coin.volatility_20d.toFixed(2)}%</li>}
            {coin.ma20_gap != null && <li>20일 평균가 대비 {coin.ma20_gap > 0 ? '+' : ''}{coin.ma20_gap.toFixed(2)}%</li>}
            {coin.range30_pos != null && (
              <li>30일 범위 안 위치 {coin.range30_pos}% (저가 {formatWon(coin.low_30d)}, 고가 {formatWon(coin.high_30d)})</li>
            )}
          </ul>
        </details>
      </div>
    </article>
  );
};

const CoinAnalysisSection = () => {
  const [range, setRange] = useState('');
  const { status, data, error, load } = useAnalysisRequest(COIN_API_URL);

  const pick = (id) => {
    setRange(id);
    load({ rank_range: id });
  };

  const coins = data?.coins || [];
  const rangeLabel = RANGES.find((r) => r.id === data?.rank_range)?.label;

  return (
    <section className="service-section">
      <h2>코인 분석 AI 서비스</h2>

      <fieldset className="field-group">
        <legend>순위</legend>
        <div className="button-container one-line">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`category-btn ${range === r.id ? 'selected' : ''}`}
              aria-pressed={range === r.id}
              onClick={() => pick(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="results paper-results" aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'idle' && <p className="paper-placeholder">순위를 고르면 바로 분석을 시작합니다.</p>}

        {status === 'loading' && (
          <>
            <p className="paper-placeholder">업비트 시세와 최근 기사를 모아 분석하고 있습니다. 20초 안팎 걸립니다.</p>
            <SkeletonCards />
          </>
        )}

        {status === 'error' && (
          <div className="paper-error">
            <p>{error}</p>
            <button type="button" className="category-btn" onClick={() => load({ rank_range: range })}>다시 시도</button>
          </div>
        )}

        {status === 'done' && data && (
          <>
            <div className="paper-header">
              <h3>업비트 {rangeLabel} 분석</h3>
              <ul className="paper-chips">
                <li>{formatKst(data.generated_at)} 기준</li>
                <li>실시간 시세</li>
                {data.cached && <li>{Math.max(1, Math.round((data.age_seconds || 0) / 60))}분 전 분석</li>}
              </ul>
              {data.overview && <p className="paper-overview">{data.overview}</p>}
              {!data.explained && (
                <p className="paper-notice">AI 분석을 만들지 못해 시세와 근거 기사만 보여 드립니다.</p>
              )}
            </div>

            <div className="paper-list">
              {coins.map((c) => <CoinCard key={c.market} coin={c} />)}
            </div>

            {data.disclaimer && <p className="mkt-disclaimer">{data.disclaimer}</p>}
          </>
        )}
      </div>
    </section>
  );
};

export default CoinAnalysisSection;
